import React, { useEffect, useMemo, useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { GripVertical, Plus, Search } from "lucide-react";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../../../../components/ui/card";
import { ScrollArea } from "../../../../components/ui/scroll-area";
import StatusBadge from "../../components/StatusBadge";
import { CANVAS_BLOCK_SLOTS } from "../compositionSlots";

/**
 * EmailBuilderAssetLibrary — EMAILS-IMPL-015
 *
 * The LEFT card: the governed Asset library, restricted to the six V1
 * draggable Canvas body families (CRE-SPEC-001 §8.6) -- Subject/Preview/
 * Landing Page never appear here (they are fixed metadata, rendered by
 * EmailBuilderMetadata instead). Reuses the SAME per-family
 * `listFn`/`eligibleStatuses` already established by CANVAS_BLOCK_SLOTS
 * (compositionSlots.js) -- no second Asset data layer, and the exact
 * same production-eligibility gate already enforced by
 * AssetVersionSlotField (5-state families: Approved/Control; 6-state:
 * Approved/Active).
 *
 * Every item is BOTH a true drag source (useDraggable, so an operator can
 * drop it at a precise Canvas position -- §12/§13) AND has an explicit
 * "Add" button that appends it to the end of the Canvas in one click.
 * This is not a reduced fallback: it is a first-class, keyboard-operable
 * way to compose a Creative without a pointer at all (§50), a common
 * pattern in production drag/drop builders, and it does not reduce what
 * true drag/drop can do (precise positional insertion remains drag-only).
 */
export default function EmailBuilderAssetLibrary({ presentFamilies, onQuickAdd, disabled }) {
  const [assetsByKey, setAssetsByKey] = useState({});
  const [status, setStatus] = useState("loading"); // loading | ready | unavailable
  const [search, setSearch] = useState("");
  const [familyFilter, setFamilyFilter] = useState("ALL");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const entries = await Promise.all(
          CANVAS_BLOCK_SLOTS.map(async (slot) => {
            const rows = await slot.listFn();
            return [slot.key, (rows || []).filter((row) => slot.eligibleStatuses.has(row.status))];
          })
        );
        if (!cancelled) {
          setAssetsByKey(Object.fromEntries(entries));
          setStatus("ready");
        }
      } catch {
        if (!cancelled) setStatus("unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleSlots = familyFilter === "ALL" ? CANVAS_BLOCK_SLOTS : CANVAS_BLOCK_SLOTS.filter((s) => s.family === familyFilter);
  const term = search.trim().toLowerCase();

  return (
    <Card className="glass flex h-full flex-col border-white/10" data-testid="builder-library">
      <CardHeader className="space-y-3 pb-3">
        <CardTitle className="text-base text-slate-100">Assets</CardTitle>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or governed ID"
            className="h-8 pl-8 text-sm"
            data-testid="builder-library-search-input"
          />
        </div>
        <div className="flex flex-wrap gap-1" data-testid="builder-library-family-filters">
          <FamilyPill label="All" active={familyFilter === "ALL"} onClick={() => setFamilyFilter("ALL")} testId="builder-library-family-filter-ALL" />
          {CANVAS_BLOCK_SLOTS.map((slot) => (
            <FamilyPill key={slot.family} label={slot.label} active={familyFilter === slot.family} onClick={() => setFamilyFilter(slot.family)} testId={`builder-library-family-filter-${slot.family}`} />
          ))}
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden p-0">
        {status === "loading" && <p className="p-4 text-sm text-slate-500">Loading Assets…</p>}
        {status === "unavailable" && <p className="p-4 text-sm text-amber-300" data-testid="builder-library-unavailable">Unable to load the Asset catalog.</p>}
        {status === "ready" && (
          <ScrollArea className="h-full px-3 pb-3">
            <div className="space-y-4">
              {visibleSlots.map((slot) => {
                const rows = (assetsByKey[slot.key] || []).filter((row) => !term || row.name.toLowerCase().includes(term) || row[slot.idField].toLowerCase().includes(term));
                if (familyFilter === "ALL" && rows.length === 0 && !term) return null;
                return (
                  <div key={slot.key} data-testid={`builder-library-group-${slot.family}`}>
                    <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">{slot.label}</div>
                    {rows.length === 0 ? (
                      <p className="text-xs text-slate-600">No eligible {slot.label} Assets.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {rows.map((asset) => (
                          <LibraryItem
                            key={asset[slot.idField]}
                            slot={slot}
                            asset={asset}
                            alreadyAdded={presentFamilies.has(slot.family)}
                            disabled={disabled}
                            onQuickAdd={onQuickAdd}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}

function FamilyPill({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        "rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors " +
        (active ? "border-violet-400/40 bg-violet-500/20 text-violet-200" : "border-white/10 text-slate-400 hover:bg-white/5")
      }
    >
      {label}
    </button>
  );
}

function LibraryItem({ slot, asset, alreadyAdded, disabled, onQuickAdd }) {
  const dragId = `library:${slot.key}:${asset[slot.idField]}`;
  const isInert = disabled || alreadyAdded;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId,
    disabled: isInert,
    data: { type: "library-item", key: slot.key, family: slot.family, assetId: asset[slot.idField], version: asset.current_version },
  });

  return (
    <div
      ref={setNodeRef}
      data-testid={`builder-library-item-${slot.family}-${asset[slot.idField]}`}
      className={
        "flex items-center gap-2 rounded-md border border-white/10 bg-white/5 p-2 text-sm " +
        (isInert ? "opacity-50" : "") + (isDragging ? " opacity-30" : "")
      }
    >
      <button
        type="button"
        className={"shrink-0 rounded p-0.5 text-slate-500 " + (isInert ? "cursor-not-allowed" : "cursor-grab hover:text-slate-300")}
        aria-label={`Drag ${asset.name} onto the Canvas`}
        disabled={isInert}
        {...attributes}
        {...listeners}
        data-testid={`builder-library-drag-handle-${slot.family}-${asset[slot.idField]}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-slate-200">{asset.name}</div>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <span className="font-mono">{asset[slot.idField]}</span>
          <span>@ {asset.current_version}</span>
        </div>
      </div>
      <StatusBadge status={asset.status} testId={`builder-library-status-${slot.family}-${asset[slot.idField]}`} />
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-7 w-7 shrink-0 p-0 text-violet-300 hover:bg-violet-500/10"
        title={alreadyAdded ? "Already added" : `Add ${asset.name} to the Canvas`}
        disabled={isInert}
        onClick={() => onQuickAdd(slot.key, { assetId: asset[slot.idField], version: asset.current_version })}
        data-testid={`builder-library-add-${slot.family}-${asset[slot.idField]}`}
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
}
